/**
 * How each channel compares with human writing (DESIGN UD-24, ED-126; docs/plan/HUMAN-REFERENCE-DESIGN.md): the human reference
 * (loaded on first use, a chunk of its own), the category selector, the coloured cell of one comparison, and the legend. The
 * statistics are `src/shared/human-reference.ts`'s; this file only shows them. The colour comes from discrete hue steps in the
 * style sheet (`data-hue`), so the page needs no inline styles; the band is always given in words too. Text nodes only.
 */
import { signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import {
	type HumanComparison,
	type HumanCountSummary,
	type HumanReference,
	defaultStrata,
	humanCounts,
	humanDocCounts,
	humanWindowDocs,
} from '../../shared/human-reference.ts';
import { fmt1, fmtInt } from '../format.ts';
import { loadHumanStrata, saveHumanStrata } from '../prefs.ts';
import { HUMAN_REF as H } from '../strings-bench.ts';

let loading: Promise<HumanReference> | null = null;

/** The reference, loaded once per page (a dynamic import, so it is not in the initial bundle). */
export function loadHumanReference(): Promise<HumanReference> {
	loading ??= import('../data/human-reference.json').then(
		(m) => m.default as unknown as HumanReference
	);
	// a failed load may be retried on the next opening
	loading.catch(() => {
		loading = null;
	});
	return loading;
}

/**
 * The viewer's categories, shared by the before/after overview and the Channels view: null until the reference is first
 * loaded, then the remembered choice (or the default). `setHumanStrata` changes and remembers it.
 */
export const humanStrata = signal<Set<string> | null>(null);

export function setHumanStrata(next: Set<string>): void {
	saveHumanStrata([...next]);
	humanStrata.value = next;
}

export function useHumanReference(): { ref: HumanReference | null; error: boolean } {
	const [state, setState] = useState<{ ref: HumanReference | null; error: boolean }>({
		ref: null,
		error: false,
	});
	useEffect(() => {
		let live = true;
		loadHumanReference().then(
			(ref) => {
				if (humanStrata.peek() === null) humanStrata.value = initialStrata(ref);
				if (live) setState({ ref, error: false });
			},
			() => live && setState({ ref: null, error: true })
		);
		return () => {
			live = false;
		};
	}, []);
	return state;
}

/** The viewer's categories: the remembered choice (only ids the reference knows), else every non-translation category. */
export function initialStrata(ref: HumanReference): Set<string> {
	const known = new Set(ref.strata.map((s) => s.id));
	const saved = loadHumanStrata()?.filter((id) => known.has(id));
	return new Set(saved ?? defaultStrata(ref));
}

export function HumanSelector({
	reference,
	selected,
	onChange,
}: {
	reference: HumanReference;
	selected: ReadonlySet<string>;
	onChange: (next: Set<string>) => void;
}) {
	const set = (next: Set<string>) => onChange(next);
	const toggle = (id: string) => {
		const next = new Set(selected);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		set(next);
	};
	return (
		<fieldset class="hr-strata" data-testid="hr-strata">
			<legend>{H.selector}</legend>
			<div class="hr-strata-options">
				{[...reference.strata]
					.sort(
						(a, b) =>
							Number(a.translation) - Number(b.translation) ||
							(H.strata[a.id] ?? a.id).localeCompare(H.strata[b.id] ?? b.id)
					)
					.map((s) => (
						<label class="check-label" key={s.id}>
							<input
								type="checkbox"
								class="check"
								checked={selected.has(s.id)}
								data-stratum={s.id}
								onChange={() => toggle(s.id)}
							/>
							<span>{H.stratumOption(H.strata[s.id] ?? s.id, fmtInt(s.n))}</span>
						</label>
					))}
			</div>
			<div class="hr-strata-actions">
				<button
					type="button"
					class="btn-text"
					data-testid="hr-all"
					onClick={() => set(new Set(defaultStrata(reference)))}
				>
					{H.all}
				</button>
				<button type="button" class="btn-text" data-testid="hr-none" onClick={() => set(new Set())}>
					{H.none}
				</button>
			</div>
		</fieldset>
	);
}

/** The hue step of the style sheet: 0 (green, 120°) to 12 (red, 0°), in steps of 10°. */
export const hueStep = (hue: number): number =>
	Math.max(0, Math.min(12, Math.round((120 - hue) / 10)));

/** The percentile as text: whole numbers in the middle, one decimal in the outer 1%. */
export function fmtPercentile(p: number): string {
	if (p > 99 || p < 1) return fmt1(Math.min(99.9, Math.max(0.1, p)));
	return String(Math.round(p));
}

/** Where the score falls, in words: "p97", "above all 433". */
export function humanWhere(c: Extract<HumanComparison, { status: 'measured' }>): string {
	if (c.beyond === 'above') return H.aboveAll(fmtInt(c.n));
	if (c.beyond === 'below') return H.belowAll(fmtInt(c.n));
	return H.pct(fmtPercentile(c.percentile));
}

/** One comparison's cell: the coloured position, or why there is none. */
export function HumanCell({ c, testId }: { c: HumanComparison | null; testId: string }) {
	if (c === null) return <span class="dim">…</span>;
	if (c.status === 'not-applicable') return <span class="dim">–</span>;
	if (c.status === 'unmeasured')
		return (
			<span class="dim hr-unmeasured" data-testid={testId} title={H.unmeasuredHelp(fmtInt(c.n))}>
				{H.unmeasured}
			</span>
		);
	const where = humanWhere(c);
	const arrow = c.band === 'within' ? '' : c.side === 'above' ? '↑ ' : '↓ ';
	const label = H.cell(H.bands[c.band], where, fmtInt(c.n));
	return (
		<span
			class="hr-chip"
			data-band={c.band}
			data-hue={hueStep(c.hue)}
			data-testid={testId}
			title={label}
			aria-label={label}
		>
			{arrow}
			{where}
		</span>
	);
}

/** The human range of one side: "14.2–31.8 · n 433" (the overview shows the after side's; the Channels view the document's). */
export function HumanRange({
	c,
	help = H.rangeHelp,
}: { c: HumanComparison | null; help?: string }) {
	if (c === null) return <span class="dim">…</span>;
	if (c.status === 'not-applicable') return <span class="dim">–</span>;
	if (c.status === 'unmeasured')
		return (
			<span class="dim" title={H.unmeasuredHelp(fmtInt(c.n))}>
				{H.unmeasured}
			</span>
		);
	return (
		<span class="hr-range" title={help}>
			{H.range(fmt1(c.range[0]), fmt1(c.range[1]))}
			<span class="dim"> · {H.n(fmtInt(c.n))}</span>
		</span>
	);
}

export function HumanLegend() {
	return (
		<p class="hr-legend dim" data-testid="hr-legend">
			<span class="hr-chip" data-band="within" data-hue="0">
				{H.legend.within}
			</span>{' '}
			<span class="hr-chip" data-band="outside" data-hue="6">
				{H.legend.outside}
			</span>{' '}
			<span class="hr-chip" data-band="far" data-hue="12">
				{H.legend.far}
			</span>{' '}
			· {H.legend.up}, {H.legend.down}
		</p>
	);
}

/**
 * The text's red and outside counts among the human documents of about its length (UD-26). Each human document's own
 * counts take about half a millisecond, and a short text's window holds hundreds, so they are computed in slices of
 * about 12 ms between frames (no long task); the summary is null until then.
 */
export function useHumanCounts(
	reference: HumanReference | null,
	comparisons: ReadonlyArray<HumanComparison | null>,
	words: number | null,
	strata: ReadonlySet<string> | null
): HumanCountSummary | null {
	const [summary, setSummary] = useState<HumanCountSummary | null>(null);
	// the counts depend on the bands only, so a key of them stands in for the comparisons
	const bands = comparisons
		.map((c) => (c?.status === 'measured' ? c.band : (c?.status ?? '-')))
		.join(',');
	// biome-ignore lint/correctness/useExhaustiveDependencies: the bands key stands for the comparisons (a new array each render)
	useEffect(() => {
		setSummary(null);
		if (!reference || !strata || words === null) return;
		let live = true;
		const done = comparisons.filter((c): c is HumanComparison => c !== null);
		const docs = humanWindowDocs(reference, words, strata);
		let at = 0;
		const step = () => {
			if (!live) return;
			const t0 = performance.now();
			while (at < docs.length && performance.now() - t0 < 12) {
				humanDocCounts(reference, docs[at] as number, strata);
				at++;
			}
			if (at < docs.length) window.setTimeout(step, 0);
			else setSummary(humanCounts(reference, done, words, strata));
		};
		window.setTimeout(step, 0);
		return () => {
			live = false;
		};
	}, [reference, strata, words, bands]);
	return summary;
}

/** The count line: how many channels are red and outside, and how often human documents of this length have as many. */
export function HumanCountLine({
	side,
	summary,
	testId,
}: { side?: string; summary: HumanCountSummary | null; testId: string }) {
	const C = H.counts;
	if (summary === null)
		return (
			<p class="hr-counts dim" data-testid={testId}>
				{side ?? ''}
				{C.pending}
			</p>
		);
	const own =
		summary.outside === 0
			? C.noneOutside(side ?? '', fmtInt(summary.measured))
			: C.own(
					side ?? '',
					C.channels(summary.far),
					C.channels(summary.outside),
					fmtInt(summary.measured)
				);
	let share = '';
	if (summary.status === 'unmeasured') share = C.unmeasured(fmtInt(summary.n));
	else if (summary.outside > 0)
		share =
			summary.far > 0
				? C.share(
						fmtInt(summary.n),
						fmtInt(summary.far),
						C.pct(summary.shareFar),
						fmtInt(summary.outside),
						C.pct(summary.shareOutside)
					)
				: C.shareOutsideOnly(
						fmtInt(summary.n),
						fmtInt(summary.outside),
						C.pct(summary.shareOutside)
					);
	return (
		<p class="hr-counts" data-testid={testId} title={C.help}>
			<strong>{own}</strong>
			{share ? ` ${share}` : ''}
		</p>
	);
}
